import { useBaseParam } from '../hooks/useBaseParam';
import { getCustPreProductList } from '../../infra/api/getCustPreProductList';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import * as Atom from '../recoil';
import useNav from '../hooks/useNav';
import { PAGE_NM } from '../types/types';
import useBeacon from '../hooks/useBeacon';
import { beaconSelector } from '../recoil/selector';
import { fetchMonthlyRequestDetail } from '../../infra/api/fetchMonthlyRequestDetail';
import { useParams } from 'react-router-dom';
import { useBridge } from '../hooks/useBridge';
import { useTerms } from '../hooks/useTerms';

const useResultStatus = () => {
  const { media } = useParams();
  const br: any = useBridge();

  const beaconData = useRecoilValue(beaconSelector);
  const beacon = useBeacon({
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    srcCd: beaconData.srcCd,
    pageName: PAGE_NM.LOAN_INFO,
  });

  const sendBeaconClkBnrNotiAutoRec = (btnNm: string) => {
    beacon.sendClkBnrNotiAutoRec({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBnrNotiSchAlrtApp = (btnNm: string) => {
    beacon.sendClkBnrNotiSchAlrtApp({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnLoanPrdPrg = (btnNm: string) => {
    beacon.sendClkBtnLoanPrdPrg({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnLoanPrdHit = (btnNm: string) => {
    beacon.sendClkBtnLoanPrdHit({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnLoanPrdComplete = (btnNm: string) => {
    beacon.sendClkBtnLoanPrdComplete({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnLoanApp = (btnNm: string) => {
    beacon.sendClkBtnLoanApp({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnAgree = (btnNm: string) => {
    beacon.sendClkBtnAgree({
      properties: { btnNm },
    });
  };

  const [personal, setPersonalInfo] = useRecoilState(Atom.personalInfo);
  const [loanList, setLoanList] = useRecoilState(Atom.loanList);
  const [loan, setLoan] = useState<any>(undefined);
  const [monthlyDetail, setMonthlyDetail] = useState<any>({});
  const [, setNotiDetailInfo] = useRecoilState(Atom.notiDetailInfo);
  const { navigate } = useNav();

  const navigateDetail = (requestSeq?: number, preRequestDtlNo?: number) => {
    navigate(`/detail/${requestSeq ?? 0}/${preRequestDtlNo}`);
  };
  const navigateNoti = (v?: string) => {
    navigate(`/${v}`);
  };
  const closeWebview = () => {
    br?.close();
  };

  const navigateNativeMain = () => {
    br?.goNativeMain();
  };
  const fetchCustLoanList = useBaseParam(getCustPreProductList, {
    custSeq: personal.custSeq,
  });

  const initLoanInfo = async () => {
    const res = await fetchCustLoanList();
    setLoan(res);
  };

  useEffect(() => {
    if (personal.custSeq) {
      initLoanInfo();
    }
  }, [personal.custSeq]);

  const initCustLoanList = () => {
    if (loan) {
      const list = loan?.data?.result;
      setLoanList((prev: any) => {
        return {
          ...prev,
          preRequestInfoChk: list.preRequestInfoChk,
          dayAgo: list.dayAgo,
          toDayRemainTime: list.toDayRemainTime,
          minInterestProductItem: {
            ...list.minInterestProductItem,
            state: 'interest',
            key: list?.minInterestProductItem?.productSeq + 'interest',
          },
          maxAmountProductItem: {
            ...list.maxAmountProductItem,
            state: 'limit',
            key: list?.minInterestProductItem?.productSeq + 'limit',
          },
          practiceProductItemList: list?.practiceProductItemList?.map(
            (item: any) => {
              return {
                key: item.preRequestDtlNo + 'practiceProductItemList',
                chip: item.hashTagList.map((chipItem: any) => ({
                  color: chipItem.color,
                  backgroundColor: chipItem.backgroundColor,
                  text: chipItem.tagNm,
                })),
                companyNm: item.compNm,
                benefitText: item.benefit,
                productNm: item.productNm,
                limitValue: item.amountText,
                amount: item.amount,
                interestRate: item.interestRate,
                logoImgUrl: item.logoImgUrl,
                preRequestDtlNo: item.preRequestDtlNo,
              };
            },
          ),
          failProductItemList: list.failProductItemList,
          searchedPreProductList: list?.searchedPreProductList?.map(
            (item: any) => {
              return {
                key: item.preRequestDtlNo + 'searchedPreProductList',
                state: 'enabled',
                logoImgUrl: item.logoImgUrl,
                productNm: item.productNm,
                companyNm: item.compNm,
                preRequestDtlNo: item.preRequestDtlNo,
                requestSeq: item.requestSeq,
              };
            },
          ),
          applyingProductList: list?.applyingProductList?.map((item: any) => {
            return {
              key: item.productSeq + 'applyingProductList',
              state: 'enabled',
              logoImgUrl: item.logoImgUrl,
              productNm: item.productNm,
              companyNm: item.compNm,
              preRequestDtlNo: item.preRequestDtlNo,
              requestSeq: item.requestSeq,
              chip: item.status
                ? [
                    {
                      type:
                        item.status === '01'
                          ? 'apply'
                          : item.status === '02'
                          ? 'examine'
                          : item.status === '03'
                          ? 'complete'
                          : '',
                    },
                  ]
                : [],
            };
          }),
          cancelProductList: list?.cancelProductList?.map((item: any) => {
            return {
              key: item.requestSeq + 'cancelProductList',
              companyNm: item.compNm,
              productNm: item.productNm,
              state: 'disabled',
              logoImgUrl: item.logoImgUrl,
              requestSeq: item.requestSeq,
            };
          }),
          practiceProductList: list?.practiceProductList?.map((item: any) => {
            return {
              key: item.requestSeq + 'practiceProductList',
              companyNm: item.compNm,
              productNm: item.productNm,
              state: 'enabled',
              logoImgUrl: item.logoImgUrl,
              requestSeq: item.requestSeq,
            };
          }),
          rejectProductList: list?.rejectProductItemList?.map((item: any) => {
            return {
              key: item.requestSeq + 'rejectProductList',
              companyNm: item.compNm,
              productNm: item.productNm,
              state: 'disabled',
              logoImgUrl: item.logoImgUrl,
              requestSeq: item.requestSeq,
            };
          }),
          todayRequested: list?.preRequestInfoChk && list?.dayAgo === 0,
        };
      });
    }
  };
  const initUserInfo = () => {
    if (loan) {
      const transactionNo = loan?.data.transactionNo;
      setPersonalInfo((prev: any) => {
        return {
          ...prev,
          regDate: loan?.data?.result?.preRequestInfoData?.regDate,
          transactionNo: transactionNo,
        };
      });
    }
  };

  const getMonthlyRequestDetail = useBaseParam(fetchMonthlyRequestDetail, {
    custSeq: personal.custSeq,
  });

  const initNotiInfo = async (val: string) => {
    const res = await getMonthlyRequestDetail();
    setMonthlyDetail(res.data.result);
    setNotiDetailInfo(res.data.result);
  };

  useEffect(() => {
    if (!!personal.custSeq) {
      initNotiInfo(personal.custSeq);
    }
  }, [personal.custSeq]);

  useEffect(() => {
    initCustLoanList();
    initUserInfo();
  }, [loan]);

  const getMonthlyBannerContent = () => {
    const monthlyBannerContents = [
      {
        message: `${monthlyDetail?.monthlyRequestRegDate}에 신청하신 정기 알림 결과입니다.\n 나의 입력 정보 확인하기 >`,
        handler: () => {
          sendBeaconClkBnrNotiAutoRec('정기 알림 결과 배너');
          navigateNoti('notiPreInforms');
        },
      },
      {
        message: `매달 변하는 나의 금리/한도를 1초 만에?\n 정기 알림 신청하기 >`,
        handler: () => {
          sendBeaconClkBnrNotiSchAlrtApp('정기 알림 신청 배너');
          navigateNoti('notiPreReg');
        },
      },
    ];

    // 자동 가심사 결과인지 여부
    const isAutoPreReq = loanList?.preRequestInfoData?.monthlyYn === 'Y';

    // 정기 알림 신청자 여부
    const isMonthlyRequestYn = monthlyDetail?.monthlyRequestYn === 'Y';

    if (isAutoPreReq && isMonthlyRequestYn) return monthlyBannerContents[0];
    if (!isMonthlyRequestYn) return monthlyBannerContents[1];
  };

  // terms
  const terms = useTerms();

  return {
    beacon,
    terms,
    isLoading: !loan,

    isPreReq: loanList?.todayRequested,
    isAutoPreReq: loanList?.preRequestInfoData?.monthlyYn === 'Y', // 자동 가심사 결과인지 여부
    isMonthlyRequestYn: monthlyDetail?.monthlyRequestYn === 'Y', // 정기 알림 신청자 여부
    monthlyBannerContent: getMonthlyBannerContent(),
    regDate: personal.regDate,
    remainTime: loanList?.toDayRemainTime?.split(':'),

    loanList,
    hasPreRequestInfo: loanList?.preRequestInfoChk,
    hasApplyingProductList: loanList?.applyingProductList?.length > 0,
    hasPracticeProductList: loanList?.practiceProductList?.length > 0,
    hasCancelProductList: loanList?.cancelProductList?.length > 0,
    hasSearchedPreProductList: loanList?.searchedPreProductList?.length > 0,
    hasPracticeProductItemList: loanList?.practiceProductItemList?.length > 0,
    hasRejectProductList: loanList?.rejectProductList?.length > 0,
    hasBestProductList:
      loanList?.todayRequested &&
      [
        ...loanList?.cancelProductList,
        ...loanList?.applyingProductList,
        ...loanList?.practiceProductItemList,
        ...loanList?.searchedPreProductList,
        ...loanList?.practiceProductList,
      ].length > 0,
    hasNoLoanList:
      loanList?.todayRequested &&
      !loanList?.practiceProductItemList.length &&
      !loanList?.applyingProductList.length &&
      !loanList?.practiceProductList.length &&
      !loanList?.cancelProductList.length &&
      !loanList?.searchedPreProductList.length,

    navigateDetail,
    closeWebview,
    navigateNativeMain,

    sendBeaconClkBtnLoanPrdPrg,
    sendBeaconClkBtnLoanPrdHit,
    sendBeaconClkBtnLoanPrdComplete,
    sendBeaconClkBtnLoanApp,
    sendBeaconClkBtnAgree,
  };
};

export default useResultStatus;
